import React, { useState, useEffect } from 'react';

import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEditTournamentStyles } from './style';
import { TournamentCalendarModel } from '../../models/tournamentCalendarModel';
import { createUpdateTournamentReq } from '../../api/tournamentCalendar/editApi';
import { getTournamentDetailsReq } from '../../api/tournamentCalendar/listApi';
import { listPersonsReq } from '../../api/person/listApi';
import { useFormik, getIn, FieldArray, FormikProvider, FieldArrayRenderProps } from 'formik';
import { validationSchema } from './validation';
import { DatePicker } from '../../components/datePicker';
import { Grid, TextField, Button, Typography } from '@material-ui/core';
import MaterialTable from 'material-table';
import { SelectOfficial } from '../../components/selectOfficial/selectOfficial';
import { TournamentRoles } from '../../models/eums/tournamentRoles';
import { AddItemToolbar } from '../../components/addItemDataTableToolbar/addItemToolbar';
import { PersonRole } from '../../models/eums/personRole';
import { PersonModel } from '../../models/personModel';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { TournamentAssigneeModel } from '../../models/tournamentAssigneeModel';
import { showError } from '../../services/notification';
import { AssigneeModel } from '../../models/assigneeModel';
import { BackButton } from '../../components/backButton/backButton';
import { localization } from '../../config/localization';
import { useHistory } from 'react-router-dom';
import moment from 'moment';

export function EditTournamentDetails() {
  const { t } = useTranslation();
  const styles = useEditTournamentStyles();
  const { tournamentId } = useParams<{ tournamentId: string }>();
  const [tournament, setTournament] = useState<TournamentCalendarModel>();
  const [officials, setOfficials] = useState<PersonModel[]>();
  const history = useHistory();

  const formikBag = useFormik<TournamentCalendarModel>({
    initialValues: tournament || {},
    onSubmit: submit,
    validationSchema,
    enableReinitialize: true,
  });

  const {
    values,
    touched,
    errors,
    handleChange,
    setFieldTouched,
    setFieldValue,
    submitForm,
    isValid,
  } = formikBag;

  useEffect(() => {
    async function getTournamentDetails() {
      if (!tournamentId?.trim()) return;
      const tournamentResp = await getTournamentDetailsReq(tournamentId.trim());
      if (tournamentResp && tournamentResp.data) setTournament(tournamentResp.data);
    }
    getTournamentDetails();
  }, [setTournament, tournamentId]);

  useEffect(() => {
    async function getOfficials() {
      const officialsResp = await listPersonsReq(PersonRole.Referee);
      if (officialsResp && officialsResp.data) setOfficials(officialsResp.data);
    }
    getOfficials();
  }, [setOfficials]);

  function getHelperText(fieldPath: string) {
    if (getIn(touched, fieldPath)) return getIn(errors, fieldPath) || '';

    return '';
  }

  function isFieldError(fieldPath: string) {
    return getIn(touched, fieldPath) && !!getIn(errors, fieldPath);
  }

  function findStateOfficialIndex(official: TournamentAssigneeModel) {
    if (!values.officials) return -1;

    return values.officials.findIndex(arrOfficial => {
      if (
        arrOfficial.person.personUUID.personId === official.person.personUUID.personId &&
        arrOfficial.role === official.role
      )
        return true;

      return false;
    });
  }

  function addOfficial(official: TournamentAssigneeModel, arrayHelpers: FieldArrayRenderProps) {
    return new Promise<void>((resolve, reject) => {
      if (!official.role || !official.person) {
        reject('Incomplete data');
        showError(t('all_fields_required'));
        return;
      }

      const duplicateOfficialIndex = findStateOfficialIndex(official);

      if (duplicateOfficialIndex >= 0) {
        reject('Already exists');
        showError(t('duplicate_assignment'));
        return;
      }

      arrayHelpers.push(official);
      resolve();
    });
  }

  function editOfficial(
    editedOfficial: TournamentAssigneeModel,
    arrayHelpers: FieldArrayRenderProps,
    official?: TournamentAssigneeModel,
  ) {
    return new Promise<void>((resolve, reject) => {
      if (!editedOfficial.role || !editedOfficial.person || !official) {
        reject('Incomplete data');
        showError(t('all_fields_required'));
        return;
      }

      const editOfficialIndex = findStateOfficialIndex(official);

      if (editOfficialIndex < 0) {
        reject('Not found');
        showError(t('error'));
        return;
      }

      arrayHelpers.replace(editOfficialIndex, editedOfficial);
      resolve();
    });
  }

  function deleteOfficial(official: TournamentAssigneeModel, arrayHelpers: FieldArrayRenderProps) {
    return new Promise<void>((resolve, reject) => {
      if (!official || !values.officials) {
        reject('Incomplete data');
        showError(t('all_fields_required'));
        return;
      }

      const deleteOfficialIndex = findStateOfficialIndex(official);
      if (deleteOfficialIndex < 0) {
        reject('Not found');
        showError(t('error'));
        return;
      }

      arrayHelpers.remove(deleteOfficialIndex);
      resolve();
    });
  }

  function assignCandidateToTournament(candidate?: AssigneeModel) {
    if (!candidate) return;
  }

  async function submit(tournament: TournamentCalendarModel) {
    const updateTournamentResp = await createUpdateTournamentReq(tournament, tournamentId);
    if (updateTournamentResp && updateTournamentResp.data) {
      setTournament(updateTournamentResp.data);
      history.push('/tournament-calendar');
    }
  }

  return (
    <Grid container direction='column' spacing={3}>
      <Grid container item direction='column' spacing={3} lg={6}>
        <Grid item>
          <BackButton tooltipTitle={t('back')} />
        </Grid>
        <Grid item>
          <TextField
            required
            fullWidth
            name='name'
            error={isFieldError('name')}
            variant='outlined'
            onChange={handleChange}
            value={values.name || ''}
            placeholder={t('tournament_name')}
            label={t('tournament_name')}
            onBlur={() => setFieldTouched('name')}
            helperText={t(getHelperText('name'))}
          />
        </Grid>
        <Grid item>
          <DatePicker
            required
            className={styles.datePicker}
            error={isFieldError('start')}
            onBlur={() => setFieldTouched('start')}
            margin='normal'
            id='date-picker-start'
            variant='inline'
            inputVariant='outlined'
            label={t('start_date')}
            format='dd.MM.yyyy.'
            value={values.start || null}
            onOpen={() => setFieldTouched('start')}
            helperText={t(getHelperText('start'))}
            onChange={(date, val) => {
              if (val) {
                //Strict parse date to ensure correct user imput
                const mDate = moment.utc(val, 'DD.MM.YYYY.', true);
                setFieldValue('start', mDate.toDate().toUTCString(), true);
              } else setFieldValue('start', undefined, true);
            }}
          />
        </Grid>
        <Grid item>
          <DatePicker
            required
            className={styles.datePicker}
            error={isFieldError('end')}
            onBlur={ev => setFieldTouched('end')}
            onOpen={() => setFieldTouched('end')}
            margin='normal'
            id='date-picker-end'
            variant='inline'
            inputVariant='outlined'
            label={t('end_date')}
            format='dd.MM.yyyy.'
            value={values.end || null}
            helperText={t(getHelperText('end'))}
            onChange={(date, val) => {
              if (val) {
                const mDate = moment.utc(val, 'DD.MM.YYYY.', true);
                setFieldValue('end', mDate.toDate().toUTCString(), true);
              } else setFieldValue('end', undefined, true);
            }}
          />
        </Grid>
        <Grid item>
          <TextField
            required
            name='rank'
            error={isFieldError('rank')}
            variant='outlined'
            onChange={handleChange}
            value={values.rank || ''}
            placeholder={t('tournament_rank')}
            label={t('tournament_rank')}
            onBlur={() => setFieldTouched('rank')}
            helperText={t(getHelperText('rank'))}
          />
        </Grid>
        <Grid item>
          <TextField
            required
            name='category'
            error={isFieldError('category')}
            variant='outlined'
            onChange={handleChange}
            value={values.category || ''}
            placeholder={t('tournament_category')}
            label={t('tournament_category')}
            onBlur={() => setFieldTouched('category')}
            helperText={t(getHelperText('category'))}
          />
        </Grid>

        <Grid item>
          <SelectOfficial
            options={officials || []}
            width={'50%'}
            value={(values.tournamentDirector as PersonModel) || null}
            label={t('referee')}
            variant='outlined'
            onChange={(ev: object, val: any) => setFieldValue('tournamentDirector', val)}
          />
        </Grid>
        <Grid item>
          <TextField
            fullWidth
            multiline
            value={values.notes || ''}
            name='notes'
            label={t('notes')}
            variant='outlined'
            onChange={handleChange}
          />
        </Grid>
      </Grid>

      <Grid item xs={12}>
        <FormikProvider value={formikBag}>
          <FieldArray name='officials'>
            {arrayHelpers => (
              <Grid container direction='row' spacing={2}>
                <Grid item xs={12} lg={6}>
                  <Typography variant='h5' gutterBottom>
                    {t('officials')}
                  </Typography>
                  <MaterialTable<TournamentAssigneeModel>
                    options={{
                      pageSize: 5,
                      showTitle: false,
                      search: false,
                      headerStyle: {
                        fontWeight: 'bold',
                        backgroundColor: '#F5F5F5',
                      },
                    }}
                    localization={localization()}
                    editable={{
                      onRowAdd: (newRow: TournamentAssigneeModel) =>
                        addOfficial(newRow, arrayHelpers),
                      onRowUpdate: (
                        newRow: TournamentAssigneeModel,
                        oldRow?: TournamentAssigneeModel,
                      ) => editOfficial(newRow, arrayHelpers, oldRow),
                      onRowDelete: (deleteRow: TournamentAssigneeModel) =>
                        deleteOfficial(deleteRow, arrayHelpers),
                    }}
                    columns={[
                      {
                        title: t('official'),
                        field: 'person',

                        render: val =>
                          `${val.person.preferredGivenName} ${val.person.preferredFamilyName}`,
                        editComponent: props => (
                          <SelectOfficial
                            options={officials || []}
                            width={'100%'}
                            value={props.value || null}
                            onChange={(ev: object, val: any) => props.onChange(val)}
                          />
                        ),
                      },
                      {
                        title: t('role'),
                        field: 'role',
                        editComponent: props => (
                          <Autocomplete
                            options={Object.values(TournamentRoles)}
                            style={{ width: '100%' }}
                            onChange={(ev: object, val: any) => props.onChange(val)}
                            value={props.value || null}
                            renderInput={(params: any) => (
                              <TextField style={{ width: '100%' }} {...params} variant='standard' />
                            )}
                          />
                        ),
                      },
                    ]}
                    data={values.officials || []}
                    components={{
                      Toolbar: props => <AddItemToolbar {...props} />,
                    }}
                  />
                </Grid>
                <Grid item xs={12} lg={6}>
                  <Typography variant='h5' gutterBottom>
                    {t('candidates')}
                  </Typography>
                  <MaterialTable<AssigneeModel>
                    options={{
                      pageSize: 5,
                      showTitle: false,
                      toolbar: false,
                      search: false,
                      headerStyle: {
                        fontWeight: 'bold',
                        backgroundColor: '#F5F5F5',
                        height: '113px',
                        verticalAlign: 'bottom',
                      },
                    }}
                    localization={localization()}
                    onRowClick={(event, candidate) => assignCandidateToTournament(candidate)}
                    columns={[
                      {
                        title: t('official'),
                        field: 'person',
                        render: val => `${val.preferredGivenName} ${val.preferredFamilyName}`,
                      },
                      {
                        title: t('application_date'),
                        field: 'date',
                        render: val => moment(val.timestamp).format('DD.MM.YYYY. HH:MM:SS'),
                      },
                    ]}
                    data={values.candidates || []}
                  />
                </Grid>
              </Grid>
            )}
          </FieldArray>
        </FormikProvider>
      </Grid>
      <Grid item>
        <Button color='primary' variant='contained' onClick={submitForm} disabled={!isValid}>
          {t('submit')}
        </Button>
      </Grid>
    </Grid>
  );
}
